import type { MongoAbility } from '@casl/ability'

export type Actions =
  | 'manage'
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'approve'
  | 'reject'
  | 'submit'

export type Subjects =
  | 'all'
  // Default landing page — every authenticated user can read it, whatever
  // their roles (see `defineAbilitiesFor`). Hidden from the sidebar.
  | 'home'
  | 'dashboard'
  | 'donors'
  // Реестр специализированных доноров (BS-236).
  | 'specialized-donors'
  | 'donations'
  | 'dictionary'
  | 'dictionary-analysis'
  // Реестр организаций (BS-264) — BLOOD_ADMIN only, via `manage all`.
  | 'dictionary-organizations'
  | 'laboratory'
  | 'labarotaroyResearch'
  | 'reports'
  | 'settings'
  | 'warehouse'
  | 'warehouse-containers'
  | 'warehouse-applications'
  | 'warehouse-issuance'
  | 'warehouse-stock'
  | 'contracts'
  | 'processing'
  // «Реципиент» sidebar group — readable by anyone who holds one of its pages.
  | 'recipients-section'
  | 'recipients'
  | 'transfusions'
  // Протокол расхода крови (BS-257).
  | 'blood-consumption'
  | 'expedition'
  | 'emergency-notifications'

export interface Permission {
  action: Actions
  subject: Subjects
}

export type AppAbility = MongoAbility<[Actions, Subjects]>

/**
 * Lab work is split in two. Bench roles (one per laboratory type) accept and
 * reject tubes and type results in — submitting a result only moves the
 * analysis to IN_CHECK, never to DONE. The lab doctor and the lab manager are
 * the only roles allowed to close that state: `approve` (IN_CHECK → DONE) and
 * `reject` (send back for a retest, which returns the analysis to the bench
 * roles for editing).
 */
const LAB_BENCH_PERMISSIONS: Permission[] = [
  // `/` redirects to the dashboard, so every lab role needs to be able to read
  // it or the post-login landing bounces to /forbidden.
  { action: 'read', subject: 'laboratory' },
  { action: 'update', subject: 'laboratory' },
  { action: 'submit', subject: 'laboratory' },
  // Экстренное извещение (форма №129): the lab staff group raises the document
  // and hands it to ИС ЭМИЗ — `create` gates both, `read` only the journal.
  { action: 'read', subject: 'emergency-notifications' },
  { action: 'create', subject: 'emergency-notifications' },
]

const LAB_REVIEW_PERMISSIONS: Permission[] = [
  ...LAB_BENCH_PERMISSIONS,
  { action: 'approve', subject: 'laboratory' },
  { action: 'reject', subject: 'laboratory' },
]

/**
 * Переработка and the receiving modules sit behind one role today — an
 * expedition employee opens the quality-control journal with the same rights.
 */
const PROCESSING_PERMISSIONS: Permission[] = [
  // `/` redirects to the dashboard, so the landing page must stay readable.
  { action: 'read', subject: 'processing' },
  { action: 'create', subject: 'processing' },
  { action: 'update', subject: 'processing' },
  { action: 'read', subject: 'expedition' },
  { action: 'create', subject: 'expedition' },
  { action: 'update', subject: 'expedition' },
  // Выдача (BS-259): every issuance endpoint, the receiver's inbox included,
  // sits behind PROCESSING_DOCTOR until a dedicated warehouse role is agreed.
  // `create` gates «Сформировать и выдать» and «Подтвердить получение».
  { action: 'read', subject: 'warehouse' },
  { action: 'read', subject: 'warehouse-issuance' },
  { action: 'create', subject: 'warehouse-issuance' },
  { action: 'read', subject: 'warehouse-stock' },
  // Отдел заготовки reads the извещение journal without acting on it; the
  // journal is a tab of Лабораторные исследования, so the section opens too —
  // read-only, which leaves the analyses and ПЦР tabs hidden.
  { action: 'read', subject: 'laboratory' },
  { action: 'read', subject: 'emergency-notifications' },
]

/**
 * Реципиенты and гемотрансфузии are the transfusion doctor's screens — the
 * patients an institution requests components for, and the protocols of the
 * doses actually transfused into them.
 */
const RECIPIENT_PERMISSIONS: Permission[] = [
  // `/` redirects to the dashboard, so the landing page must stay readable.
  { action: 'read', subject: 'recipients-section' },
  { action: 'read', subject: 'recipients' },
  { action: 'create', subject: 'recipients' },
  { action: 'update', subject: 'recipients' },
  { action: 'read', subject: 'transfusions' },
  { action: 'create', subject: 'transfusions' },
]

/**
 * Протокол расхода крови: a medical institution records the doses it used in
 * an operation. A blood-centre user of these roles gets `100160` from the
 * endpoints and the screen explains that instead of showing the form.
 */
const BLOOD_CONSUMPTION_PERMISSIONS: Permission[] = [
  { action: 'read', subject: 'recipients-section' },
  { action: 'read', subject: 'blood-consumption' },
  { action: 'create', subject: 'blood-consumption' },
]
const BLOOD_WAREHOUSE_PERMISSIONS: Permission[] = [
  // { action: 'read', subject: 'dashboard' },
  { action: 'manage', subject: 'warehouse' },
  // { action: 'manage', subject: 'warehouse-containers' },
  // { action: 'manage', subject: 'warehouse-applications' },
  // { action: 'manage', subject: 'warehouse-issuance' },
  { action: 'manage', subject: 'warehouse-stock' },
]

export const rolePermissions: Record<string, Permission[]> = {
  patient: [],
  practitioner: [],
  VIEW_FULL_PROFILE: [],
  BLOOD_ORG_ADMIN: [
    { action: 'read', subject: 'specialized-donors' },
    ...BLOOD_CONSUMPTION_PERMISSIONS,
  ],
  BLOOD_ADMIN: [{ action: 'manage', subject: 'all' }],
  INTERNAL_SERVICE: [],
  BLOOD_DONATION_DOCTOR: [],
  BLOOD_LAB_DOCTOR: LAB_REVIEW_PERMISSIONS,
  BLOOD_LAB_MANAGER: LAB_REVIEW_PERMISSIONS,
  BLOOD_CBL_LAB: LAB_BENCH_PERMISSIONS,
  BLOOD_HTI_LAB: LAB_BENCH_PERMISSIONS,
  BLOOD_BACT_LAB: LAB_BENCH_PERMISSIONS,
  BLOOD_IMMUNO_LAB: LAB_BENCH_PERMISSIONS,
  BLOOD_PROCESSING_DOCTOR: PROCESSING_PERMISSIONS,
  // Read-only on the извещение journal, which is a tab of Лабораторные
  // исследования — hence `read laboratory` to reach the page at all. Without
  // `update` the analyses and ПЦР tabs stay hidden, and without `create` on the
  // journal the raise/send buttons do too, so nothing is left to a 403.
  BLOOD_THERAPIST: [
    { action: 'read', subject: 'dashboard' },
    { action: 'read', subject: 'laboratory' },
    { action: 'read', subject: 'emergency-notifications' },
    { action: 'read', subject: 'specialized-donors' },
    ...BLOOD_CONSUMPTION_PERMISSIONS,
  ],
  BLOOD_TRANSFUSION_DOCTOR: RECIPIENT_PERMISSIONS,
  // Склад: full control over every warehouse page — containers, applications,
  // issuance and stock.
  BLOOD_WAREHOUSE: [
    // `/` redirects to the dashboard, so the landing page must stay readable.
    ...BLOOD_WAREHOUSE_PERMISSIONS,
  ],
  BLOOD_WAREHOUSE_MANAGER: [...BLOOD_WAREHOUSE_PERMISSIONS],
  BLOOD_DONOR: [],
  BLOOD_SERVICE_OFFICER: [],
  BLOOD_REGISTRAR: [
    { action: 'manage', subject: 'all' },
    { action: 'read', subject: 'specialized-donors' },
    ...BLOOD_CONSUMPTION_PERMISSIONS,
  ],
}

export type Role = keyof typeof rolePermissions

export function getPermissions(role: Role): Permission[] {
  return rolePermissions[role] ?? []
}
